/**
 * Merges per-source usage summaries into the single view the page renders.
 *
 * Two things can report the same tokens, and both are handled here:
 *
 * 1. Two environments on one machine (worktree servers, for example) resolve
 *    the same provider home. The first environment in a stable order claims
 *    each transcript directory; the rest have that provider's buckets dropped.
 * 2. A Forge turn is held by CMMD's ledger and reported again by the
 *    environment that ran it as a Recorded turn. CMMD is canonical: when it
 *    answered, an environment's `acknowledged` Recorded buckets are dropped
 *    and CMMD's rows stand. `pending` buckets (CMMD has not acknowledged them
 *    yet) and buckets with no sync marker (no replication lane) are kept,
 *    and when CMMD did not answer every Recorded bucket stands in for it.
 *    An acknowledged `subagent` bucket is dropped for the same reason, so the
 *    forge-agents lane shrinks to the pending and unrecorded remainder as
 *    subagent rows reach the ledger.
 *    Transcript buckets are the CLI sessions Forge did not run; the scan
 *    already dropped the ones that belong to Forge threads. A transcript
 *    bucket marked `driver: "forge"` is agent work Forge drove; while no
 *    Recorded turn and no ledger row carries it, it is totalled on its own
 *    lane (`usageMergeLanes`) and never folded into CMMD's figures.
 *
 * The page also splits the total by what each row is a row of, which CMMD
 * reports beside its buckets because a turn count cannot be derived from them.
 * See {@link foldReportedKinds}.
 *
 * Pure, so the de-duplication and derivation rules can be tested without a
 * connected environment.
 *
 * @module usageMerge
 */
import { USAGE_MERGE_COMPATIBLE_SINCE } from "@cmmd-forge/contracts";
import type {
  UsageBucket,
  UsageKind,
  UsageProviderKind,
  UsageSourceFingerprint,
  UsageSummary,
} from "@cmmd-forge/contracts";

import {
  EMPTY_LANE_ROWS,
  bucketTokens,
  emptyLaneState,
  finishLanes,
  foldLanes,
  kindOf,
  laneOf,
  type CostProvenance,
  type DriverTotals,
  type EnvironmentTotals,
  type LaneState,
  type LaneTotals,
  type RuntimeOwnership,
} from "./usageMergeLanes";

export type {
  CostProvenance,
  DriverTotals,
  EnvironmentTotals,
  LaneTotals,
  UsageLane,
} from "./usageMergeLanes";

export interface EnvironmentUsage {
  /** Stable identity of the reporting source: an environment id, or `cmmd`. */
  readonly sourceId: string;
  readonly label: string;
  readonly summary: UsageSummary;
}

export interface ProviderTotals {
  readonly provider: UsageProviderKind;
  readonly costUsd: number;
  readonly totalTokens: number;
  readonly records: number;
  /**
   * Distinct transcript sessions reported by directories belonging to this
   * harness. Attributed from the reporting fingerprint, not from buckets: a
   * session spans days and models, so folding it per bucket would overcount.
   */
  readonly sessions: number;
  readonly costShare: number;
  readonly tokenShare: number;
}

export interface ModelTotals {
  readonly model: string;
  readonly provider: UsageProviderKind;
  readonly costUsd: number;
  readonly totalTokens: number;
  readonly records: number;
  readonly costShare: number;
  /** Records whose tokens are counted here but whose rate was unknown. */
  readonly unpricedRecords: number;
  /**
   * Records that carry no tokens at all. Zero also stands for a window no
   * server measured, which is why {@link CostQuality.unreportedRecordsKnown}
   * is what decides whether the page may name a count.
   */
  readonly unreportedRecords: number;
}

/**
 * Totals for one kind of Usage row across every source the page is showing.
 *
 * `records` counts rows, so a turn that spans two models counts twice. `turns`
 * counts distinct turn ids and is `null` when nothing that contributed to this
 * kind holds any: a transcript scan reads session files, which carry no turn
 * id, so the forge-agents lane can say what it cost but not how many turns it
 * belonged to.
 */
export interface KindTotals {
  readonly kind: UsageKind;
  readonly costUsd: number;
  readonly totalTokens: number;
  readonly records: number;
  readonly turns: number | null;
  /** Share of the page total, the way the model rows read. */
  readonly costShare: number;
}

/** The order the page reads kinds in, most of the money first. */
const KIND_ORDER: readonly UsageKind[] = ["turn", "subagent", "text_generation"];

export interface DailyTotals {
  readonly day: string;
  readonly costUsd: number;
  readonly totalTokens: number;
  readonly byProvider: ReadonlyMap<UsageProviderKind, { costUsd: number; totalTokens: number }>;
}

export interface HourlyTotals {
  readonly day: string;
  readonly hourStart: string;
  readonly costUsd: number;
  readonly totalTokens: number;
  readonly byProvider: ReadonlyMap<UsageProviderKind, { costUsd: number; totalTokens: number }>;
}

export interface CostQuality {
  readonly providerReportedShare: number;
  readonly modelPricedShare: number;
  readonly unpricedShare: number;
  readonly cacheSavingsUsd: number;
  /** Records across the window whose tokens are known and whose rate is not. */
  readonly unpricedRecords: number;
  /**
   * Records across the window that carry no tokens, so nothing could be
   * priced. Zero until {@link unreportedRecordsKnown} is true, because a
   * server that omits the field measured nothing rather than measuring none.
   */
  readonly unreportedRecords: number;
  /**
   * Whether any bucket carried an unreported count. False on a fleet whose
   * servers all predate the field, and the page hides the clause rather than
   * printing a zero nobody measured.
   */
  readonly unreportedRecordsKnown: boolean;
}

export interface MergedUsage {
  readonly costUsd: number;
  readonly uncachedInputTokens: number;
  readonly cachedInputTokens: number;
  readonly cacheCreationTokens: number;
  readonly outputTokens: number;
  readonly reasoningTokens: number;
  readonly totalTokens: number;
  readonly records: number;
  readonly sessions: number;
  readonly providers: readonly ProviderTotals[];
  readonly models: readonly ModelTotals[];
  readonly daily: readonly DailyTotals[];
  readonly hourly: readonly HourlyTotals[];
  readonly costQuality: CostQuality;
  /**
   * One row per kind, always all three, in reading order. The page hides the
   * table when only `turn` holds anything, so a fleet with no subagent and no
   * text generation work is not shown a split that says nothing.
   */
  readonly kinds: readonly KindTotals[];
  readonly environments: readonly EnvironmentTotals[];
  /** Recorded turns CMMD has not acknowledged yet. */
  readonly pendingSync: LaneTotals;
  /** CLI sessions Forge did not run, scanned from transcripts. */
  readonly localOnly: LaneTotals;
  /**
   * Agent work Forge drove that no Recorded turn carries: Claude subagent
   * turns and Codex subagent rollouts under a Forge thread. Estimated on the
   * environment that scanned them; never part of CMMD's figures.
   */
  readonly forgeAgents: LaneTotals;
  /** The Local-only lane split by the harness that drove each session. */
  readonly drivers: readonly DriverTotals[];
  readonly costProvenance: CostProvenance;
  /** Sources whose data was dropped as a duplicate of another's. */
  readonly duplicateSources: readonly string[];
  /**
   * Runtimes whose acknowledged buckets were dropped because CMMD's ledger
   * holds the same rows, whether those were Recorded turns or subagent work.
   * Surfaced so the page can explain the number, not because anything is
   * missing from it.
   */
  readonly claimedRuntimeIds: readonly string[];
  readonly contributingSources: readonly string[];
  readonly staleSources: readonly string[];
}

/**
 * Two sources are the same physical transcript directory only when kind, host,
 * provider, path and filesystem identity all agree.
 *
 * `volumeId` is what stops two machines that happen to share a hostname and a
 * home path, which is every Mac in a fleet, from collapsing into one source and
 * having one of them silently dropped.
 */
function fingerprintKey(fingerprint: UsageSourceFingerprint): string {
  return [
    fingerprint.kind,
    fingerprint.hostId,
    fingerprint.provider,
    fingerprint.resolvedHomePath,
    fingerprint.volumeId,
  ].join(" ");
}

interface SourceClaims {
  readonly ownerByFingerprint: ReadonlyMap<string, string>;
  readonly duplicates: readonly string[];
  /**
   * Whether CMMD's ledger answered. Only then is an acknowledged Recorded
   * bucket a duplicate of something already counted; a failed or absent CMMD
   * read leaves every Recorded bucket standing in for it.
   */
  readonly cmmdAnswered: boolean;
}

/**
 * Decides which source owns each physical transcript directory, and whether
 * CMMD's ledger is present to be canonical.
 *
 * Sources are sorted by id so the winner does not change between renders.
 */
function claimSources(sources: readonly EnvironmentUsage[]): SourceClaims {
  const ownerByFingerprint = new Map<string, string>();
  const duplicates: string[] = [];
  let cmmdAnswered = false;

  const ordered = sources.toSorted((a, b) => a.sourceId.localeCompare(b.sourceId));

  const claim = (
    source: EnvironmentUsage,
    reported: EnvironmentUsage["summary"]["sources"][number],
  ) => {
    if (reported.status === "missing" || reported.status === "failed") return;
    const key = fingerprintKey(reported.fingerprint);
    if (ownerByFingerprint.has(key)) {
      duplicates.push(`${source.label}: ${reported.fingerprint.resolvedHomePath}`);
      return;
    }
    ownerByFingerprint.set(key, source.sourceId);
    if (reported.fingerprint.kind === "cmmd") cmmdAnswered = true;
  };

  for (const source of ordered) {
    for (const reported of source.summary.sources) claim(source, reported);
  }

  return { ownerByFingerprint, duplicates, cmmdAnswered };
}

/**
 * Decides which source speaks for each runtime, so every lane of one machine
 * lands on one row.
 *
 * A source names its runtimes on its own fingerprints. On a desktop only the
 * `recorded` fingerprint carries one, the desktop relay runtime id, so the
 * join exists whenever the machine has Recorded turns in the window; a cloud
 * runtime names it on its transcript fingerprints too. CMMD's fingerprint is
 * skipped: CMMD holds ledger rows for every runtime in the workspace and
 * speaks for none of them, and honouring its claim would fold the whole fleet
 * onto CMMD's row. The web label resolver reads the same fingerprints, in
 * `labelReportedRuntimes`.
 *
 * Sources are sorted by id so the winner does not change between renders,
 * matching {@link claimSources}.
 */
function claimRuntimes(sources: readonly EnvironmentUsage[]): RuntimeOwnership {
  const ownerByRuntimeId = new Map<string, string>();
  const runtimeIdBySourceId = new Map<string, string>();
  for (const source of sources.toSorted((a, b) => a.sourceId.localeCompare(b.sourceId))) {
    for (const runtimeId of reportedRuntimeIds(source)) {
      claimRuntime(ownerByRuntimeId, runtimeIdBySourceId, runtimeId, source.sourceId);
    }
  }
  return { ownerByRuntimeId, runtimeIdBySourceId };
}

/** The runtime ids a source names on its own fingerprints, CMMD's excluded. */
function reportedRuntimeIds(source: EnvironmentUsage): readonly string[] {
  return source.summary.sources
    .filter((reported) => reported.fingerprint.kind !== "cmmd")
    .flatMap((reported) => reported.runtimeIds);
}

/** First claim wins; a source keeps the first runtime it claimed as its own. */
function claimRuntime(
  ownerByRuntimeId: Map<string, string>,
  runtimeIdBySourceId: Map<string, string>,
  runtimeId: string,
  sourceId: string,
): void {
  if (ownerByRuntimeId.has(runtimeId)) return;
  ownerByRuntimeId.set(runtimeId, sourceId);
  if (!runtimeIdBySourceId.has(sourceId)) {
    runtimeIdBySourceId.set(sourceId, runtimeId);
  }
}

/**
 * A local bucket whose rows CMMD's ledger already holds.
 *
 * Two shapes qualify, and both mean the same thing: this environment marked
 * the rows acknowledged and CMMD answered, so CMMD's own rows are the
 * canonical copy. A Recorded bucket is the original case. A `subagent` bucket
 * is the case FOR-36420 creates: once subagent rows reach the ledger, the
 * environment that scanned them marks them acknowledged and the forge-agents
 * lane shrinks to what CMMD has not taken. Until then no scanner sets `sync`
 * on that lane, nothing is acknowledged, and the lane is unchanged.
 */
function isSupersededByCmmd(bucket: UsageBucket, claims: SourceClaims): boolean {
  if (!claims.cmmdAnswered || bucket.sync !== "acknowledged") return false;
  return bucket.origin === "recorded" || kindOf(bucket) === "subagent";
}

/** The reported sources this environment won the claim for. */
function ownedSources(
  source: EnvironmentUsage,
  claims: SourceClaims,
): readonly EnvironmentUsage["summary"]["sources"][number][] {
  return source.summary.sources.filter(
    (reported) =>
      reported.status !== "missing" &&
      reported.status !== "failed" &&
      claims.ownerByFingerprint.get(fingerprintKey(reported.fingerprint)) === source.sourceId,
  );
}

/** Buckets this source owns after fingerprint claims, plus its session counts. */
function ownedContribution(
  source: EnvironmentUsage,
  claims: SourceClaims,
): {
  readonly buckets: readonly UsageBucket[];
  readonly sessionsByProvider: ReadonlyMap<UsageProviderKind, number>;
  readonly ownedKinds: ReadonlySet<string>;
} {
  const ownedProviders = new Set<UsageProviderKind>();
  const ownedKinds = new Set<string>();
  // Keyed by provider so the summary panel can print a session count beside
  // each harness. The directory that reported a session is what attributes it;
  // buckets cannot, because one session spans several of them.
  const sessionsByProvider = new Map<UsageProviderKind, number>();
  for (const reported of ownedSources(source, claims)) {
    const provider = reported.fingerprint.provider;
    ownedProviders.add(provider);
    ownedKinds.add(reported.fingerprint.kind);
    // A Recorded source counts threads, and CMMD counts the same threads once
    // it holds them; only one of the two may attribute them.
    if (reported.fingerprint.kind === "recorded" && claims.cmmdAnswered) continue;
    // CMMD is one ledger, not one directory per harness. It reports a single
    // fingerprint under the `cmmd` provider while the contract has it report
    // brokered calls under the harness that ran them, `claude` or `codex`.
    // Matching lanes to the fingerprint's own provider therefore dropped every
    // brokered bucket. A transcript source is the opposite case: it proves a
    // lane by scanning that lane's directory, so it keeps the narrow rule and
    // two environments sharing a provider home still de-duplicate.
    if (reported.fingerprint.kind === "cmmd") {
      for (const bucket of source.summary.buckets) ownedProviders.add(bucket.provider);
    }
    // Distinct within a directory. Summing per-bucket session counts instead
    // would count a session once per day and model it spans.
    sessionsByProvider.set(
      provider,
      (sessionsByProvider.get(provider) ?? 0) + reported.distinctSessions,
    );
  }
  return {
    buckets: source.summary.buckets.filter(
      (bucket) => ownedProviders.has(bucket.provider) && !isSupersededByCmmd(bucket, claims),
    ),
    sessionsByProvider,
    ownedKinds,
  };
}

/** Reads `key`, seeding it first when absent. */
function upsert<K, V>(map: Map<K, V>, key: K, seed: () => V): V {
  const existing = map.get(key);
  if (existing !== undefined) return existing;
  const created = seed();
  map.set(key, created);
  return created;
}

interface ProviderCell {
  costUsd: number;
  totalTokens: number;
}

interface PeriodCell extends ProviderCell {
  readonly byProvider: Map<UsageProviderKind, ProviderCell>;
}

interface HourCell extends PeriodCell {
  readonly day: string;
  readonly hourStart: string;
}

function emptyProviderCell(): ProviderCell {
  return { costUsd: 0, totalTokens: 0 };
}

function emptyProviderTotalsCell(): ProviderCell & { records: number; sessions: number } {
  return { costUsd: 0, totalTokens: 0, records: 0, sessions: 0 };
}

function addToPeriod(
  cell: PeriodCell,
  provider: UsageProviderKind,
  costUsd: number,
  tokens: number,
): void {
  cell.costUsd += costUsd;
  cell.totalTokens += tokens;
  const byProvider = upsert(cell.byProvider, provider, emptyProviderCell);
  byProvider.costUsd += costUsd;
  byProvider.totalTokens += tokens;
}

/** Running totals across every owned bucket from every source. */
interface MergeState {
  costUsd: number;
  uncachedInputTokens: number;
  cachedInputTokens: number;
  cacheCreationTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  records: number;
  sessions: number;
  cacheSavingsUsd: number;
  providerReportedRecords: number;
  unpricedRecords: number;
  unreportedRecords: number;
  /** Set by the first folded bucket that carried an unreported count. */
  unreportedRecordsKnown: boolean;
  readonly providers: Map<UsageProviderKind, ProviderCell & { records: number; sessions: number }>;
  readonly models: Map<string, ProviderCell & ModelCoverageCell>;
  readonly daily: Map<string, PeriodCell>;
  readonly hourly: Map<string, HourCell>;
  readonly kinds: Map<UsageKind, KindCell>;
  readonly lanes: LaneState;
}

/** What a model cell keeps beyond its money, so the page can say how much of it is priced. */
interface ModelCoverageCell {
  readonly provider: UsageProviderKind;
  records: number;
  unpricedRecords: number;
  unreportedRecords: number;
}

interface KindCell {
  costUsd: number;
  totalTokens: number;
  records: number;
  /** Null until a source that holds turn ids contributes to this kind. */
  turns: number | null;
}

function emptyKindCell(): KindCell {
  return { costUsd: 0, totalTokens: 0, records: 0, turns: null };
}

function emptyMergeState(): MergeState {
  return {
    costUsd: 0,
    uncachedInputTokens: 0,
    cachedInputTokens: 0,
    cacheCreationTokens: 0,
    outputTokens: 0,
    reasoningTokens: 0,
    records: 0,
    sessions: 0,
    cacheSavingsUsd: 0,
    providerReportedRecords: 0,
    unpricedRecords: 0,
    unreportedRecords: 0,
    unreportedRecordsKnown: false,
    providers: new Map(),
    models: new Map(),
    daily: new Map(),
    hourly: new Map(),
    kinds: new Map(),
    lanes: emptyLaneState(),
  };
}

/**
 * Folds a source's own per-kind totals, which is the only way a turn count
 * survives the trip.
 *
 * Reported beside the buckets rather than derived from them: the totals cover
 * the same rows, so folding both would double the money. This is why only a
 * source whose buckets the merge never drops may be read this way. CMMD is
 * that source: its buckets lose no provider to the ownership filter and are
 * never superseded, since it is CMMD that supersedes.
 */
function foldReportedKinds(state: MergeState, kinds: UsageSummary["kinds"]): void {
  for (const reported of kinds ?? []) {
    const cell = upsert(state.kinds, reported.kind, emptyKindCell);
    cell.costUsd += reported.costUsd;
    cell.totalTokens +=
      reported.totals.uncachedInputTokens +
      reported.totals.cachedInputTokens +
      reported.totals.cacheCreationTokens +
      reported.totals.outputTokens;
    cell.records += reported.records;
    cell.turns = (cell.turns ?? 0) + reported.turns;
  }
}

/**
 * Folds one bucket into its kind. Contributes no turn count: a bucket is a
 * `(day, provider, model)` cell, so its rows may belong to one turn or to
 * several and it cannot tell which.
 */
function foldBucketKind(state: MergeState, bucket: UsageBucket): void {
  const cell = upsert(state.kinds, kindOf(bucket), emptyKindCell);
  cell.costUsd += bucket.costUsd;
  cell.totalTokens += bucketTokens(bucket);
  cell.records += bucket.records;
}

/**
 * Folds one source's kinds, from whichever of its two accounts may speak.
 *
 * A source that reported per-kind totals speaks for its own kinds and its
 * buckets must not be folded in as well: the totals re-aggregate the same rows,
 * so reading both would double the money. Only CMMD reports them, and only
 * CMMD may be read this way. A transcript scan has no turn ids to count, and a
 * local source's buckets can be dropped as superseded, which a whole-window
 * total cannot follow.
 */
function foldSourceKinds(
  state: MergeState,
  summary: UsageSummary,
  buckets: readonly UsageBucket[],
  ownedKinds: ReadonlySet<string>,
): void {
  const reported = ownedKinds.has("cmmd") ? summary.kinds : undefined;
  if (reported !== undefined) {
    foldReportedKinds(state, reported);
    return;
  }
  for (const bucket of buckets) foldBucketKind(state, bucket);
}

/**
 * Folds one source's session counts into the running totals.
 *
 * Runs before that source's buckets so a harness that reported sessions still
 * gets a summary row even when every one of its buckets was claimed elsewhere.
 */
function foldSessions(
  state: MergeState,
  sessionsByProvider: ReadonlyMap<UsageProviderKind, number>,
): void {
  for (const [provider, sessions] of sessionsByProvider) {
    state.sessions += sessions;
    upsert(state.providers, provider, emptyProviderTotalsCell).sessions += sessions;
  }
}

function foldBucket(state: MergeState, bucket: UsageBucket): void {
  const tokens = bucketTokens(bucket);

  state.costUsd += bucket.costUsd;
  state.cacheSavingsUsd += bucket.cacheSavingsUsd;
  state.uncachedInputTokens += bucket.totals.uncachedInputTokens;
  state.cachedInputTokens += bucket.totals.cachedInputTokens;
  state.cacheCreationTokens += bucket.totals.cacheCreationTokens;
  state.outputTokens += bucket.totals.outputTokens;
  state.reasoningTokens += bucket.totals.reasoningTokens;
  state.records += bucket.records;
  state.unpricedRecords += bucket.unpricedRecords;
  // Read once: a bucket that omits the key says nothing about unreported work,
  // and the flag is what stops the page printing a zero nobody measured.
  const unreportedRecords = bucket.unreportedRecords;
  if (unreportedRecords !== undefined) {
    state.unreportedRecords += unreportedRecords;
    state.unreportedRecordsKnown = true;
  }
  if (bucket.costSource === "providerReported") state.providerReportedRecords += bucket.records;

  const provider = upsert(state.providers, bucket.provider, emptyProviderTotalsCell);
  provider.costUsd += bucket.costUsd;
  provider.totalTokens += tokens;
  provider.records += bucket.records;

  const model = upsert(state.models, `${bucket.provider} ${bucket.model}`, () => ({
    provider: bucket.provider,
    costUsd: 0,
    totalTokens: 0,
    records: 0,
    unpricedRecords: 0,
    unreportedRecords: 0,
  }));
  model.costUsd += bucket.costUsd;
  model.totalTokens += tokens;
  model.records += bucket.records;
  model.unpricedRecords += bucket.unpricedRecords;
  model.unreportedRecords += unreportedRecords ?? 0;

  const day = upsert(state.daily, bucket.day, () => ({
    costUsd: 0,
    totalTokens: 0,
    byProvider: new Map<UsageProviderKind, ProviderCell>(),
  }));
  addToPeriod(day, bucket.provider, bucket.costUsd, tokens);

  const hourStart = bucket.hourStart;
  if (hourStart === undefined) return;
  const hour = upsert(state.hourly, hourStart, () => ({
    day: bucket.day,
    hourStart,
    costUsd: 0,
    totalTokens: 0,
    byProvider: new Map<UsageProviderKind, ProviderCell>(),
  }));
  addToPeriod(hour, bucket.provider, bucket.costUsd, tokens);
}

/**
 * Splits sources by contract version. A source running older code is excluded
 * rather than blocking the page, and its id is reported so the UI can say
 * coverage is partial.
 */
function partitionByContract(
  sources: readonly EnvironmentUsage[],
  expectedContractVersion: number,
  compatibleSince: number = USAGE_MERGE_COMPATIBLE_SINCE,
): { readonly current: readonly EnvironmentUsage[]; readonly staleSources: readonly string[] } {
  const current: EnvironmentUsage[] = [];
  const staleSources: string[] = [];
  for (const source of sources) {
    const version = source.summary.contractVersion;
    // A range, not an exact match: a sprite on the previous image or CMMD on
    // its own cadence must keep contributing instead of vanishing as stale.
    if (version >= compatibleSince && version <= expectedContractVersion) current.push(source);
    else staleSources.push(source.sourceId);
  }
  return { current, staleSources };
}

const EMPTY_MERGED: MergedUsage = {
  costUsd: 0,
  uncachedInputTokens: 0,
  cachedInputTokens: 0,
  cacheCreationTokens: 0,
  outputTokens: 0,
  reasoningTokens: 0,
  totalTokens: 0,
  records: 0,
  sessions: 0,
  providers: [],
  models: [],
  daily: [],
  hourly: [],
  costQuality: {
    providerReportedShare: 0,
    modelPricedShare: 0,
    unpricedShare: 0,
    cacheSavingsUsd: 0,
    unpricedRecords: 0,
    unreportedRecords: 0,
    unreportedRecordsKnown: false,
  },
  kinds: KIND_ORDER.map((kind) => ({
    kind,
    costUsd: 0,
    totalTokens: 0,
    records: 0,
    turns: null,
    costShare: 0,
  })),
  ...EMPTY_LANE_ROWS,
  duplicateSources: [],
  claimedRuntimeIds: [],
  contributingSources: [],
  staleSources: [],
};

/**
 * Merges every source's summary.
 *
 * `expectedContractVersion` guards against a source running older code: rather
 * than blocking the page, its data is excluded and its id is reported so the UI
 * can say coverage is partial.
 */
export function mergeUsage(
  sources: readonly EnvironmentUsage[],
  expectedContractVersion: number,
): MergedUsage {
  if (sources.length === 0) return EMPTY_MERGED;

  const { current, staleSources } = partitionByContract(sources, expectedContractVersion);
  const claims = claimSources(current);
  const runtimes = claimRuntimes(current);

  const state = emptyMergeState();
  const contributingSources: string[] = [];
  const claimedRuntimeIds = new Set<string>();

  for (const source of current) {
    const { buckets, sessionsByProvider, ownedKinds } = ownedContribution(source, claims);
    if (buckets.length > 0) contributingSources.push(source.sourceId);
    foldSessions(state, sessionsByProvider);

    for (const bucket of source.summary.buckets) {
      if (bucket.runtimeId !== undefined && isSupersededByCmmd(bucket, claims)) {
        claimedRuntimeIds.add(bucket.runtimeId);
      }
    }

    foldSourceKinds(state, source.summary, buckets, ownedKinds);

    for (const bucket of buckets) {
      foldBucket(state, bucket);
      foldLanes(state.lanes, bucket, source.sourceId, laneOf(bucket, ownedKinds), runtimes);
    }
  }

  const {
    costUsd,
    uncachedInputTokens,
    cachedInputTokens,
    cacheCreationTokens,
    outputTokens,
    reasoningTokens,
    records,
    sessions,
    cacheSavingsUsd,
    providerReportedRecords,
    unpricedRecords,
    unreportedRecords,
    unreportedRecordsKnown,
  } = state;
  const providerAccumulator = state.providers;
  const modelAccumulator = state.models;
  const dailyAccumulator = state.daily;
  const hourlyAccumulator = state.hourly;

  const totalTokens = uncachedInputTokens + cachedInputTokens + cacheCreationTokens + outputTokens;

  const providers: ProviderTotals[] = [...providerAccumulator.entries()]
    .map(([provider, totals]) => ({
      provider,
      costUsd: totals.costUsd,
      totalTokens: totals.totalTokens,
      records: totals.records,
      sessions: totals.sessions,
      costShare: costUsd === 0 ? 0 : totals.costUsd / costUsd,
      tokenShare: totalTokens === 0 ? 0 : totals.totalTokens / totalTokens,
    }))
    .toSorted((a, b) => b.costUsd - a.costUsd);

  const models: ModelTotals[] = [...modelAccumulator.entries()]
    .map(([key, totals]) => ({
      model: key.slice(key.indexOf(" ") + 1),
      provider: totals.provider,
      costUsd: totals.costUsd,
      totalTokens: totals.totalTokens,
      records: totals.records,
      costShare: costUsd === 0 ? 0 : totals.costUsd / costUsd,
      unpricedRecords: totals.unpricedRecords,
      unreportedRecords: totals.unreportedRecords,
    }))
    .toSorted((a, b) => b.costUsd - a.costUsd || b.totalTokens - a.totalTokens);

  const daily: DailyTotals[] = [...dailyAccumulator.entries()]
    .map(([day, totals]) => ({
      day,
      costUsd: totals.costUsd,
      totalTokens: totals.totalTokens,
      byProvider: totals.byProvider,
    }))
    .toSorted((a, b) => a.day.localeCompare(b.day));

  const hourly: HourlyTotals[] = [...hourlyAccumulator.values()].toSorted((a, b) =>
    a.hourStart.localeCompare(b.hourStart),
  );

  // Every kind, in reading order, whether or not it holds anything: a fixed
  // order is what lets the table be read at a glance, and the page decides on
  // its own whether the split is worth drawing at all.
  const kinds: KindTotals[] = KIND_ORDER.map((kind) => {
    const cell = state.kinds.get(kind) ?? emptyKindCell();
    return {
      kind,
      costUsd: cell.costUsd,
      totalTokens: cell.totalTokens,
      records: cell.records,
      turns: cell.turns,
      costShare: costUsd === 0 ? 0 : cell.costUsd / costUsd,
    };
  });

  const lanes = finishLanes(state.lanes, costUsd);

  return {
    costUsd,
    uncachedInputTokens,
    cachedInputTokens,
    cacheCreationTokens,
    outputTokens,
    reasoningTokens,
    totalTokens,
    records,
    sessions,
    providers,
    models,
    daily,
    hourly,
    costQuality: {
      providerReportedShare: records === 0 ? 0 : providerReportedRecords / records,
      unpricedShare: records === 0 ? 0 : unpricedRecords / records,
      // Every record in the window is the denominator, and modelPricedShare drops unreported rows.
      modelPricedShare:
        records === 0
          ? 0
          : (records - providerReportedRecords - unpricedRecords - unreportedRecords) / records,
      cacheSavingsUsd,
      unpricedRecords,
      unreportedRecords,
      unreportedRecordsKnown,
    },
    kinds,
    ...lanes,
    duplicateSources: claims.duplicates,
    claimedRuntimeIds: [...claimedRuntimeIds].toSorted(),
    contributingSources,
    staleSources,
  };
}
